import { Component, computed, effect, inject, input, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import {
  QcChecklistItemResult,
  QcChecklistResults,
  QcLabTestValue,
  createEmptyLabTestValue,
  groupChecklistItems,
  parseChecklistResults,
  parseChecklistTemplate,
  serializeChecklistResults
} from './qc-checklist.model';

@Component({
  standalone: true,
  selector: 'ain-qc-checklist-results-form',
  imports: [
    FormsModule, SelectModule, InputTextModule, InputNumberModule, TextareaModule, CheckboxModule,
    JalaliDateInputComponent
  ],
  template: `
    @if (items().length === 0) {
      <p class="hint">{{ t('catalog.qc.checklist.empty') }}</p>
    } @else {
      @for (group of groupedItems(); track group.category) {
        <section class="checklist-group">
          <h5>{{ categoryLabel(group.category) }}</h5>
          <div class="checklist-grid">
            @for (item of group.items; track item.id) {
              <article class="checklist-item" [class.lab-item]="item.type === 'lab_test'">
                <header>
                  <strong>{{ item.label }}</strong>
                  @if (item.required) {
                    <em class="req">*</em>
                  }
                  <span class="type-badge">{{ typeLabel(item.type) }}</span>
                </header>

                <div class="fields" [class.lab-fields]="item.type === 'lab_test'">
                  @switch (item.type) {
                    @case ('pass_fail') {
                      <label>
                        <span>{{ t('catalog.qc.checklist.outcome') }}</span>
                        <p-select
                          [options]="outcomeOptions()"
                          [(ngModel)]="item.outcome"
                          optionLabel="label"
                          optionValue="value"
                          [disabled]="readOnly()"
                          appendTo="body"
                          (ngModelChange)="emitChange()" />
                      </label>
                    }
                    @case ('numeric') {
                      <label>
                        <span>{{ t('catalog.qc.checklist.value') }}@if (item.unit) { ({{ item.unit }}) }</span>
                        <p-inputNumber
                          [(ngModel)]="item.value"
                          [useGrouping]="false"
                          mode="decimal"
                          [minFractionDigits]="0"
                          [maxFractionDigits]="4"
                          [disabled]="readOnly()"
                          (ngModelChange)="onNumericChange(item)" />
                      </label>
                      <label>
                        <span>{{ t('catalog.qc.checklist.outcome') }}</span>
                        <p-select
                          [options]="outcomeOptions()"
                          [(ngModel)]="item.outcome"
                          optionLabel="label"
                          optionValue="value"
                          [disabled]="readOnly()"
                          appendTo="body"
                          (ngModelChange)="emitChange()" />
                      </label>
                    }
                    @case ('boolean') {
                      <label class="bool-row">
                        <p-checkbox [binary]="true" [(ngModel)]="item.value" [disabled]="readOnly()" (ngModelChange)="onBooleanChange(item, $event)" />
                        <span>{{ t('catalog.qc.checklist.confirmed') }}</span>
                      </label>
                    }
                    @case ('text') {
                      <label class="full">
                        <span>{{ t('catalog.qc.checklist.value') }}</span>
                        <textarea pTextarea rows="2" [(ngModel)]="item.value" [disabled]="readOnly()" (ngModelChange)="emitChange()"></textarea>
                      </label>
                    }
                    @case ('lab_test') {
                      <label>
                        <span>{{ t('catalog.qc.checklist.lab.sampleReference') }}</span>
                        <input pInputText type="text" [ngModel]="labValue(item).sampleReference" (ngModelChange)="updateLabField(item, 'sampleReference', $event)" [disabled]="readOnly()" />
                      </label>
                      <label class="lab-wide">
                        <span>{{ t('catalog.qc.checklist.lab.method') }}</span>
                        <input pInputText type="text" [ngModel]="labValue(item).method" (ngModelChange)="updateLabField(item, 'method', $event)" [placeholder]="methodHint(item)" [disabled]="readOnly()" />
                      </label>
                      <label>
                        <span>{{ t('catalog.qc.checklist.lab.resultValue') }}</span>
                        <input pInputText type="text" [ngModel]="labValue(item).resultValue" (ngModelChange)="updateLabField(item, 'resultValue', $event)" [disabled]="readOnly()" />
                      </label>
                      <label>
                        <span>{{ t('catalog.qc.checklist.lab.unit') }}</span>
                        <input pInputText type="text" [ngModel]="labValue(item).unit" (ngModelChange)="updateLabField(item, 'unit', $event)" [disabled]="readOnly()" />
                      </label>
                      <label>
                        <span>{{ t('catalog.qc.checklist.lab.referenceRange') }}</span>
                        <input pInputText type="text" [ngModel]="labValue(item).referenceRange" (ngModelChange)="updateLabField(item, 'referenceRange', $event)" [disabled]="readOnly()" />
                      </label>
                      <label class="lab-wide">
                        <span>{{ t('catalog.qc.checklist.lab.labReportReference') }}</span>
                        <input pInputText type="text" [ngModel]="labValue(item).labReportReference" (ngModelChange)="updateLabField(item, 'labReportReference', $event)" [disabled]="readOnly()" />
                      </label>
                      <label class="lab-date">
                        <span>{{ t('catalog.qc.checklist.lab.testedAt') }}</span>
                        <ain-date-input [ngModel]="labValue(item).testedAt" (ngModelChange)="updateLabField(item, 'testedAt', $event)" [disabled]="readOnly()" />
                      </label>
                      <label class="lab-outcome">
                        <span>{{ t('catalog.qc.checklist.outcome') }}</span>
                        <p-select
                          [options]="labOutcomeOptions()"
                          [(ngModel)]="item.outcome"
                          optionLabel="label"
                          optionValue="value"
                          [disabled]="readOnly()"
                          appendTo="body"
                          (ngModelChange)="emitChange()" />
                      </label>
                    }
                  }

                  <label class="full">
                    <span>{{ t('catalog.qc.checklist.notes') }}</span>
                    <input pInputText type="text" [(ngModel)]="item.notes" [disabled]="readOnly()" (ngModelChange)="emitChange()" />
                  </label>
                </div>
              </article>
            }
          </div>
        </section>
      }
    }
  `,
  styles: [`
    .checklist-group { display: grid; gap: 10px; margin-bottom: 16px; }
    .checklist-group h5 { margin: 0; font-size: 14px; color: #1e293b; }
    .checklist-grid { display: grid; gap: 10px; }
    .checklist-item { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; background: #fff; overflow: hidden; }
    .checklist-item.lab-item { border-color: #bfdbfe; background: #f8fbff; }
    .checklist-item header { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
    .checklist-item header strong { font-size: 14px; }
    .req { color: #b91c1c; font-style: normal; }
    .type-badge { font-size: 11px; color: #64748b; background: #f1f5f9; border-radius: 999px; padding: 2px 8px; }
    .fields {
      display: grid;
      gap: 12px;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    }
    .fields.lab-fields {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
    .fields label {
      display: grid;
      gap: 6px;
      min-width: 0;
    }
    .fields label.full { grid-column: 1 / -1; }
    .fields.lab-fields label.lab-wide { grid-column: span 2; }
    .fields span { font-size: 12px; color: #475569; font-weight: 600; line-height: 1.35; }
    .bool-row { grid-template-columns: auto 1fr; align-items: center; }
    .hint { color: #64748b; font-size: 13px; margin: 0; }

    .fields :is(input, textarea) { width: 100%; min-width: 0; box-sizing: border-box; }
    .fields :is(p-select, p-inputnumber, ain-date-input) {
      display: block;
      width: 100%;
      min-width: 0;
    }

    :host ::ng-deep .fields p-select,
    :host ::ng-deep .fields p-select .p-select,
    :host ::ng-deep .fields p-select .p-select-label,
    :host ::ng-deep .fields p-inputnumber,
    :host ::ng-deep .fields p-inputnumber .p-inputnumber,
    :host ::ng-deep .fields p-inputnumber input,
    :host ::ng-deep .fields ain-date-input,
    :host ::ng-deep .fields ain-date-input input {
      width: 100%;
      min-width: 0;
      box-sizing: border-box;
    }

    @media (max-width: 1100px) {
      .fields.lab-fields { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .fields.lab-fields label.lab-wide { grid-column: span 2; }
    }

    @media (max-width: 640px) {
      .fields,
      .fields.lab-fields { grid-template-columns: 1fr; }
      .fields.lab-fields label.lab-wide { grid-column: span 1; }
    }
  `]
})
export class QcChecklistResultsFormComponent {
  private readonly localization = inject(LocalizationService);

  readonly templateJson = input('');
  readonly ruleId = input<string | null>(null);
  readonly ruleCode = input<string | null>(null);
  readonly readOnly = input(false);
  readonly resultsJson = model('{}');

  readonly items = signal<QcChecklistItemResult[]>([]);
  readonly groupedItems = computed(() => groupChecklistItems(this.items()));

  readonly outcomeOptions = () => [
    { label: this.t('catalog.qc.checklist.outcome.pass'), value: 'pass' },
    { label: this.t('catalog.qc.checklist.outcome.fail'), value: 'fail' },
    { label: this.t('catalog.qc.checklist.outcome.na'), value: 'na' }
  ];

  readonly labOutcomeOptions = () => [
    { label: this.t('catalog.qc.checklist.outcome.pass'), value: 'pass' },
    { label: this.t('catalog.qc.checklist.outcome.fail'), value: 'fail' },
    { label: this.t('catalog.qc.checklist.outcome.inconclusive'), value: 'na' }
  ];

  constructor() {
    effect(() => {
      this.localization.resources();
      this.localization.activeCulture();
      const template = parseChecklistTemplate(this.templateJson(), key => this.t(key));
      const results = parseChecklistResults(this.resultsJson(), template, {
        ruleId: this.ruleId(),
        ruleCode: this.ruleCode()
      });
      this.items.set(results.items.map(item => ({ ...item })));
    });
  }

  labValue(item: QcChecklistItemResult): QcLabTestValue {
    if (item.value && typeof item.value === 'object' && !Array.isArray(item.value)) {
      const record = item.value as QcLabTestValue;
      return {
        sampleReference: record.sampleReference ?? '',
        method: record.method ?? '',
        resultValue: record.resultValue ?? '',
        unit: record.unit ?? item.unit ?? '',
        referenceRange: record.referenceRange ?? '',
        labReportReference: record.labReportReference ?? '',
        testedAt: record.testedAt ?? null
      };
    }
    return createEmptyLabTestValue(item.unit ?? '');
  }

  updateLabField(item: QcChecklistItemResult, field: keyof QcLabTestValue, value: string | null): void {
    item.value = { ...this.labValue(item), [field]: value ?? (field === 'testedAt' ? null : '') };
    this.emitChange();
  }

  methodHint(item: QcChecklistItemResult): string {
    return item.methodHintKey ? this.t(item.methodHintKey) : '';
  }

  emitChange(): void {
    const payload: QcChecklistResults = {
      version: 1,
      ruleId: this.ruleId(),
      ruleCode: this.ruleCode(),
      items: this.items()
    };
    this.resultsJson.set(serializeChecklistResults(payload));
  }

  onNumericChange(item: QcChecklistItemResult): void {
    const value = typeof item.value === 'number' ? item.value : null;
    if (value === null) {
      item.outcome = '';
    } else if (item.minValue !== undefined && value < item.minValue) {
      item.outcome = 'fail';
    } else if (item.maxValue !== undefined && value > item.maxValue) {
      item.outcome = 'fail';
    } else if (!item.outcome || item.outcome === 'na') {
      item.outcome = 'pass';
    }
    this.emitChange();
  }

  onBooleanChange(item: QcChecklistItemResult, value: boolean): void {
    item.value = value;
    item.outcome = value ? 'pass' : 'fail';
    this.emitChange();
  }

  categoryLabel(category: string): string {
    return category === 'laboratory'
      ? this.t('catalog.qc.checklist.section.laboratory')
      : this.t('catalog.qc.checklist.section.general');
  }

  typeLabel(type: string): string {
    switch (type) {
      case 'numeric': return this.t('catalog.qc.checklist.type.numeric');
      case 'text': return this.t('catalog.qc.checklist.type.text');
      case 'boolean': return this.t('catalog.qc.checklist.type.boolean');
      case 'lab_test': return this.t('catalog.qc.checklist.type.labTest');
      default: return this.t('catalog.qc.checklist.type.passFail');
    }
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
